import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Product } from "./catalog";

// Persisted server-side, keyed by the signed-in customer (#348, ADR 0075),
// via the BFF's `GET`/`PUT /api/cart` — same-origin, cookie auth, the same
// shape `api.ts`'s checkout call already uses. `CartProvider` only ever
// mounts once signed in (main.tsx), so there's no anonymous-cart case to
// handle here.
//
// The server only ever sees `{sku, quantity}` — never price or name, which
// always come fresh from the catalog the same way an order's line items do
// (ADR 0053's "don't let a stale copy drift from the pricing authority"
// applied to the cart too). That means hydrating the stored cart needs the
// catalog already loaded to resolve each sku back into a `Product`; until
// then the cart starts empty, same as before this existed.
//
// Best-effort, last-write-wins (the issue's own stated scope): every
// mutation pushes the full current cart back to the server; a failed push
// leaves the in-memory cart — still correct for this tab — as the only
// copy until the next successful sync.

export interface CartLine {
  product: Product;
  quantity: number;
}

interface CartApi {
  lines: CartLine[];
  /** Total item count across all lines — the header badge. */
  count: number;
  /** Σ unitPrice × quantity. Display-only; order-service owns the real total. */
  subtotal: number;
  add: (product: Product, quantity?: number) => void;
  setQuantity: (sku: string, quantity: number) => void;
  remove: (sku: string) => void;
  clear: () => void;
}

interface WireLine {
  sku: string;
  quantity: number;
}

const CartContext = createContext<CartApi | null>(null);

async function fetchServerCart(): Promise<WireLine[]> {
  try {
    const res = await fetch("/api/cart", { credentials: "include" });
    if (!res.ok) {
      return [];
    }
    const body = (await res.json()) as { lines?: WireLine[] };
    return Array.isArray(body.lines) ? body.lines : [];
  } catch {
    return [];
  }
}

function saveServerCart(lines: WireLine[]): void {
  fetch("/api/cart", {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ lines }),
  }).catch(() => {
    // Best-effort (see module doc) — nothing to recover here; the next
    // mutation's sync gets another chance.
  });
}

export function CartProvider({
  products,
  children,
}: {
  /** The current catalog, for resolving a server-stored sku back into a Product. */
  products: Product[];
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const hydrated = useRef(false);
  // Set by every user mutation, never by hydration — so restoring the
  // stored cart doesn't immediately PUT it straight back.
  const mutated = useRef(false);

  useEffect(() => {
    if (hydrated.current || products.length === 0) {
      return;
    }
    hydrated.current = true;

    const bySku = new Map(products.map((p) => [p.sku, p]));
    void fetchServerCart().then((wire) => {
      const restored = wire
        .map((w): CartLine | null => {
          const product = bySku.get(w.sku);
          return product ? { product, quantity: w.quantity } : null;
        })
        .filter((l): l is CartLine => l !== null);
      // Anything added while the GET was in flight wins (and has already
      // been saved) — last-write-wins, same as across devices.
      setLines((prev) => (prev.length > 0 ? prev : restored));
    });
  }, [products]);

  // Push the full cart after each user mutation. Kept out of the setLines
  // updaters so StrictMode's double-invoked updaters don't double-PUT.
  useEffect(() => {
    if (!mutated.current) {
      return;
    }
    mutated.current = false;
    saveServerCart(lines.map((l) => ({ sku: l.product.sku, quantity: l.quantity })));
  }, [lines]);

  const add = useCallback((product: Product, quantity = 1) => {
    mutated.current = true;
    setLines((prev) => {
      const existing = prev.find((l) => l.product.sku === product.sku);
      if (existing) {
        return prev.map((l) =>
          l.product.sku === product.sku ? { ...l, quantity: l.quantity + quantity } : l,
        );
      }
      return [...prev, { product, quantity }];
    });
  }, []);

  const setQuantity = useCallback((sku: string, quantity: number) => {
    mutated.current = true;
    setLines((prev) =>
      quantity <= 0
        ? prev.filter((l) => l.product.sku !== sku)
        : prev.map((l) => (l.product.sku === sku ? { ...l, quantity } : l)),
    );
  }, []);

  const remove = useCallback((sku: string) => {
    mutated.current = true;
    setLines((prev) => prev.filter((l) => l.product.sku !== sku));
  }, []);

  const clear = useCallback(() => {
    mutated.current = true;
    setLines([]);
  }, []);

  const api = useMemo<CartApi>(() => {
    const count = lines.reduce((n, l) => n + l.quantity, 0);
    const subtotal = lines.reduce((n, l) => n + l.product.unitPrice * l.quantity, 0);
    return { lines, count, subtotal, add, setQuantity, remove, clear };
  }, [lines, add, setQuantity, remove, clear]);

  return <CartContext.Provider value={api}>{children}</CartContext.Provider>;
}

export function useCart(): CartApi {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart outside CartProvider");
  return ctx;
}
